import { type ChatMessage, compareMessages, newerReaction, type Reaction } from "./messages";

const reactionKey = (r: Pick<Reaction, "msgId" | "clientId" | "emoji">) =>
  `${r.msgId}\u0000${r.clientId}\u0000${r.emoji}`;

/** A message's reactions, grouped by emoji (in first-seen order). */
export interface ReactionSummary {
  emoji: string;
  /** Who has it on (client ids and names). */
  people: { clientId: string; name: string }[];
}

/** Where messages are kept: memory only, or memory backed by IndexedDB (per room). */
export class ChatStore {
  readonly #messages = new Map<string, ChatMessage>();
  readonly #reactions = new Map<string, Reaction>();
  #sorted: ChatMessage[] = [];
  readonly #db: IDBDatabase | undefined;

  private constructor(
    db: IDBDatabase | undefined,
    initial: ChatMessage[],
    reactions: Reaction[] = [],
  ) {
    this.#db = db;
    for (const message of initial) this.#messages.set(message.id, message);
    for (const reaction of reactions) this.#reactions.set(reactionKey(reaction), reaction);
    this.#resort();
  }

  /** Memory-only store (solo sessions, tests). */
  static memory(): ChatStore {
    return new ChatStore(undefined, []);
  }

  /** IndexedDB-backed store for a room; loads what's already saved. */
  static async open(topic: string, idb: IDBFactory = indexedDB): Promise<ChatStore> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      // v2 added reactions; a v1 database (older build) gains the store on open.
      const request = idb.open(databaseName(topic), 2);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains("messages")) {
          db.createObjectStore("messages", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("reactions")) db.createObjectStore("reactions");
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    const getAll = <T>(name: string) =>
      new Promise<T[]>((resolve, reject) => {
        const request = db.transaction(name).objectStore(name).getAll();
        request.onsuccess = () => resolve(request.result as T[]);
        request.onerror = () => reject(request.error);
      });
    return new ChatStore(
      db,
      await getAll<ChatMessage>("messages"),
      await getAll<Reaction>("reactions"),
    );
  }

  /** Every message, in the shared order. */
  all(): readonly ChatMessage[] {
    return this.#sorted;
  }

  /** The most recent messages (for history replies). */
  recent(limit: number): ChatMessage[] {
    return this.#sorted.slice(-limit);
  }

  get maxLamport(): number {
    return this.#sorted.at(-1)?.lamport ?? 0;
  }

  /** Adds messages not seen before; returns how many were new. */
  add(messages: ChatMessage[]): number {
    const fresh = messages.filter((m) => !this.#messages.has(m.id));
    if (fresh.length === 0) return 0;
    for (const message of fresh) this.#messages.set(message.id, message);
    this.#resort();
    if (this.#db) {
      const store = this.#db.transaction("messages", "readwrite").objectStore("messages");
      for (const message of fresh) store.put(message);
    }
    return fresh.length;
  }

  /** Merges reaction records (newest per message/person/emoji wins); returns how many changed. */
  addReactions(reactions: Reaction[]): number {
    const changed: Reaction[] = [];
    for (const reaction of reactions) {
      const key = reactionKey(reaction);
      const current = this.#reactions.get(key);
      const winner = current ? newerReaction(current, reaction) : reaction;
      if (winner === current) continue;
      this.#reactions.set(key, winner);
      changed.push(winner);
    }
    if (this.#db && changed.length > 0) {
      const store = this.#db.transaction("reactions", "readwrite").objectStore("reactions");
      for (const reaction of changed) store.put(reaction, reactionKey(reaction));
    }
    return changed.length;
  }

  /** The current record for one person's emoji on a message, if any. */
  reaction(msgId: string, clientId: string, emoji: string): Reaction | undefined {
    return this.#reactions.get(reactionKey({ msgId, clientId, emoji }));
  }

  /** Reaction records for these messages (for history replies). */
  reactionsFor(msgIds: Set<string>): Reaction[] {
    return [...this.#reactions.values()].filter((r) => msgIds.has(r.msgId));
  }

  get maxReactionLamport(): number {
    let max = 0;
    for (const r of this.#reactions.values()) max = Math.max(max, r.lamport);
    return max;
  }

  /** Message id → its reactions that are on, grouped by emoji. */
  reactionSummaries(): Map<string, ReactionSummary[]> {
    const out = new Map<string, ReactionSummary[]>();
    for (const r of this.#reactions.values()) {
      if (!r.on) continue;
      const list = out.get(r.msgId) ?? [];
      let group = list.find((g) => g.emoji === r.emoji);
      if (!group) {
        group = { emoji: r.emoji, people: [] };
        list.push(group);
      }
      group.people.push({ clientId: r.clientId, name: r.name });
      out.set(r.msgId, list);
    }
    return out;
  }

  close(): void {
    this.#db?.close();
  }

  #resort(): void {
    this.#sorted = [...this.#messages.values()].sort(compareMessages);
  }
}

export function databaseName(topic: string): string {
  return `codeshare:chat:${topic}`;
}

/** Deletes a room's saved chat ("Leave & forget"). */
export function forgetChat(topic: string, idb: IDBFactory = indexedDB): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = idb.deleteDatabase(databaseName(topic));
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
}
